import { useState } from 'react';
import { useField } from 'formik';

import Modal from './Modal';
import { Button } from './Button';
import { InputCheckbox } from './Inputs';
import { useAccountUser } from '../contexts/User';

interface TermsAndConditionsModalProps {
  handleCancel: () => void;
  handleAccept: (version: string) => void;
}

function TermsAndConditionsModal({ handleCancel, handleAccept }: TermsAndConditionsModalProps) {
  const { termsAndConditions } = useAccountUser();

  const [scrollPercentage, setScrollPercentage] = useState<string>('0%');
  const [viewedAll, setViewedAll] = useState<boolean>(false);

  const handleScroll = (e: any) => {
    const parent = e.target; // container div
    const elem = parent.children[0]; // child div

    const scrollPercent =
      ((elem.scrollTop || parent.scrollTop) / (parent.scrollHeight - parent.clientHeight)) * 100;

    setScrollPercentage(`${scrollPercent}%`);

    if (scrollPercent === 100 && !viewedAll) {
      setViewedAll(true);
    }
  };

  if (!termsAndConditions) return null;

  return (
    <Modal
      className="w-full md:max-w-screen-md xl:max-w-screen-lg"
      title="Terms and Conditions"
      notes="Please scroll to the bottom before clicking on the button to agree."
      body={
        <>
          {/* Portal Body */}
          <div className="flex">
            {/* Scrollbar */}
            <div className="relative flex-shrink-0 w-4 mr-4 h-96">
              {/* full height bar */}
              <div className="absolute top-0 w-1 h-full bg-gray-200 ml-1.5" />
              {/* current bar height */}
              <div
                className="absolute bg-blue-500 top-0 w-1 bg-blue-400 ml-1.5 h-1/2 transition-all ease-in-out duration-75"
                style={{
                  height: viewedAll ? '100%' : scrollPercentage,
                }}
              />

              {/* current position indicator */}
              <div className="w-full h-full py-2">
                <div className="relative w-full h-full">
                  <div
                    className="absolute left-0 flex items-center justify-center w-4 h-4 -mt-2 bg-blue-400 bg-blue-500 rounded-full transition-all ease-in-out duration-75"
                    style={{
                      top: viewedAll ? '100%' : scrollPercentage,
                    }}
                  >
                    {viewedAll && (
                      <svg
                        className="w-3 h-3"
                        fill="none"
                        stroke="white"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Text body */}
            <div
              onScroll={handleScroll}
              className="px-6 pt-10 overflow-y-scroll leading-relaxed bg-gray-100 rounded-xl no-scrollbar max-h-96"
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: termsAndConditions.content }}
            />
          </div>
        </>
      }
      footer={
        <div className="flex justify-end">
          <Button type="button" outline className="mr-2" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={!viewedAll}
            onClick={() => handleAccept(termsAndConditions.id)}
          >
            I agree to these Terms and Conditions
          </Button>
        </div>
      }
    />
  );
}

export function InputCheckboxTermsAndConditions({ name, value }: { name: string; value: string }) {
  const [, , helpers] = useField(name);
  const [showModal, setShowModal] = useState(false);

  return (
    <>
      <InputCheckbox
        name={name}
        label={
          <>
            Accept{' '}
            <span
              role="button"
              className="text-blue-500 underline hover:pointer hover:text-blue-600"
              tabIndex={-1}
              onClick={() => setShowModal(true)}
              onKeyDown={() => setShowModal(true)}
            >
              Terms and Conditions
            </span>
          </>
        }
        checked={!!value}
        value={value}
        onChange={() => {
          if (value) {
            helpers.setValue('');
          } else {
            setShowModal(true);
          }
        }}
      />

      {showModal && (
        <TermsAndConditionsModal
          handleCancel={() => setShowModal(false)} //
          handleAccept={(version) => {
            helpers.setValue(version);
            setShowModal(false);
          }}
        />
      )}
    </>
  );
}
